import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Layouts
import Layout from './components/Layout';
import TopNavLayout from './components/TopNavLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Campaigns from './pages/Campaigns';
import Reports from './pages/Reports';
import PageReports from './pages/PageReports';
import SharedReport from './pages/SharedReport';
import SsoLogin from './pages/SsoLogin';
import Leads from './pages/Leads';
import Settings from './pages/Settings';
import FranchiseReports from './pages/FranchiseReports';
import GoogleAdsReports from './pages/GoogleAdsReports';
import CombinedReports from './pages/CombinedReports';
import ClinicorpReports from './pages/ClinicorpReports';
import FranchiseReportsV2 from './pages/FranchiseReportsV2';
import Conversions from './pages/Conversions';
import PageSubscriptions from './pages/PageSubscriptions';
import LeadSourceMappings from './pages/LeadSourceMappings';
import ClinicData from './pages/ClinicData';
import ClinicReports from './pages/ClinicReports';
import ClinicForm from './pages/ClinicForm';
import Forms from './pages/Forms';
import FormEditor from './pages/FormEditor';
import FormsGuard from './components/forms/FormsGuard';

// Hooks
import useAuth from './hooks/useAuth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />

            {/* Public Shared Reports */}
            <Route path="/shared/:token" element={<SharedReport />} />

            {/* Public Chatwoot SSO handoff (Reports > Campanhas) */}
            <Route path="/sso/:token" element={<SsoLogin />} />

            {/* Public Clinic Form */}
            <Route path="/clinic-form/:token" element={<ClinicForm />} />

            {/* Protected Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/accounts" element={
              <ProtectedRoute>
                <Layout>
                  <Accounts />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/campaigns/:accountId" element={
              <ProtectedRoute>
                <Layout>
                  <Campaigns />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/reports" element={
              <ProtectedRoute>
                <Layout>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/page-reports" element={
              <ProtectedRoute>
                <Layout>
                  <PageReports />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/franchise-reports" element={
              <ProtectedRoute>
                <Layout>
                  <FranchiseReports />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/google-ads-reports" element={
              <ProtectedRoute>
                <Layout>
                  <GoogleAdsReports />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/combined-reports" element={
              <ProtectedRoute>
                <Layout>
                  <CombinedReports />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/clinicorp-reports" element={
              <ProtectedRoute>
                <Layout>
                  <ClinicorpReports />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/franchise-v2" element={
              <ProtectedRoute>
                <Layout>
                  <FranchiseReportsV2 />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/leads" element={
              <ProtectedRoute>
                <Layout>
                  <Leads />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/conversions" element={
              <ProtectedRoute>
                <Layout>
                  <Conversions />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/forms" element={
              <ProtectedRoute>
                <FormsGuard>
                  <Layout>
                    <Forms />
                  </Layout>
                </FormsGuard>
              </ProtectedRoute>
            } />

            <Route path="/forms/:id" element={
              <ProtectedRoute>
                <FormsGuard>
                  <Layout>
                    <FormEditor />
                  </Layout>
                </FormsGuard>
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/page-subscriptions" element={
              <ProtectedRoute>
                <Layout>
                  <PageSubscriptions />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/lead-source-mappings" element={
              <ProtectedRoute>
                <Layout>
                  <LeadSourceMappings />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/clinic-data" element={
              <ProtectedRoute>
                <Layout>
                  <ClinicData />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/clinic-data/reports" element={
              <ProtectedRoute>
                <Layout>
                  <ClinicReports />
                </Layout>
              </ProtectedRoute>
            } />

            {/* /crm-view — same pages as above, top-nav layout instead of sidebar. Exclusive
                to the embedded Chatwoot "Relatórios > Campanhas" iframe (see SsoLogin.jsx,
                which lands SSO'd sessions here instead of "/"). */}
            <Route path="/crm-view" element={
              <ProtectedRoute>
                <TopNavLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="campaigns/:accountId" element={<Campaigns />} />
              <Route path="reports" element={<Reports />} />
              <Route path="page-reports" element={<PageReports />} />
              <Route path="franchise-reports" element={<FranchiseReports />} />
              <Route path="google-ads-reports" element={<GoogleAdsReports />} />
              <Route path="combined-reports" element={<CombinedReports />} />
              <Route path="clinicorp-reports" element={<ClinicorpReports />} />
              <Route path="franchise-v2" element={<FranchiseReportsV2 />} />
              <Route path="leads" element={<Leads />} />
              <Route path="conversions" element={<Conversions />} />
              <Route path="forms" element={<FormsGuard><Forms /></FormsGuard>} />
              <Route path="forms/:id" element={<FormsGuard><FormEditor /></FormsGuard>} />
              <Route path="settings" element={<Settings />} />
              <Route path="page-subscriptions" element={<PageSubscriptions />} />
              <Route path="lead-source-mappings" element={<LeadSourceMappings />} />
              <Route path="clinic-data" element={<ClinicData />} />
              <Route path="clinic-data/reports" element={<ClinicReports />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>

          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4aed88',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ff6b6b',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
